/**
 * What sits on a record's entry list: the layout entry, its stable id, the
 * default layout a template implies, and the parser that reads a stored
 * layout back safely.
 *
 * Pure, so it tests under bun. `entry-repo.ts` owns the reading and writing.
 *
 * Two ideas carry this file.
 *
 * 1. **An entry's id IS the hash of what it draws.** Placing the same card
 *    over the same insight twice is meaningless, so it cannot happen: the
 *    second placement has the same id as the first. That also makes the
 *    gallery's "already added" check a plain id lookup rather than a
 *    field-by-field comparison.
 * 2. **No stored layout is not an empty layout.** A record nobody has
 *    customized shows what its template asked for, which is why
 *    `defaultLayoutFor` exists and why `entry-repo` answers null rather than
 *    `[]` when there is no row.
 * 3. **A name is not part of what a card draws.** `title` is outside the hash,
 *    so renaming a card is not a new card, and two names over one question is
 *    still one card. Renaming has its own edit, `withTitle`.
 */
import {
  readInsightAgg,
  readInsightWhere,
  readRowSource,
  type RecordSchemaResponse,
} from '@alluzo/shared';

import {
  allowsSize,
  allowsSpan,
  defaultSizeFor,
  defaultSpanFor,
  INSIGHT_CARD_SHAPE,
  isInsightCardId,
  type InsightCardId,
  type InsightSize,
  type InsightSpan,
} from '@/lib/insight-cards';
import {
  sortFilters,
  type InsightFilter,
  type InsightMeasure,
  type InsightScope,
  type InsightSpec,
  type ScalarSpec,
} from '@/lib/insight-query';

export type InsightLayoutEntry = {
  /** `specHash(card, spec)`. Stable across launches, unique by construction. */
  readonly id: string;
  readonly card: InsightCardId;
  readonly spec: InsightSpec;
  readonly span: InsightSpan;
  /**
   * The rectangle it takes on the record's grid. Beside `span`, never instead
   * of it: the Dashboard still reads the span, and a build that predates this
   * key reads one entry the same way it always did.
   *
   * Outside the id hash, like `title` — resizing an insight must not make it a
   * different insight.
   */
  readonly size: InsightSize;
  /**
   * The i18n key a template gave this card, such as `stat.this_month`. Null
   * when nothing named it and the label is derived from the spec instead.
   */
  readonly labelKey: string | null;
  /**
   * A name the person typed. Free text the tenant owns, so it can never be a
   * catalogue key. It wins over `labelKey` and over the derived sentence, and
   * it is deliberately outside the hash — see rule 3 in the header.
   */
  readonly title: string | null;
};

/* -------------------------------------------------------------------- hash */

/**
 * A stable string for one (card, spec) pair.
 *
 * JSON.stringify follows insertion order, and two specs built by different
 * code paths can hold the same fields in a different order. Sorting every key
 * is what makes the hash a fact about the value rather than about how it was
 * assembled.
 *
 * Exported because the business layout hashes its own specs the same way.
 * `specHash` itself is untouched, so every id already stored on a device stays
 * byte-identical.
 */
export const canonical = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, held]) => held !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([key, held]) => `${JSON.stringify(key)}:${canonical(held)}`).join(',')}}`;
};

export const specHash = (card: InsightCardId, spec: InsightSpec): string =>
  canonical({ card, spec });

/** The spec without its `filters` key at all, so the hash cannot see one. */
const omitFilters = (spec: InsightSpec): InsightSpec => {
  if (spec.filters === undefined) return spec;
  const { filters: _dropped, ...rest } = spec;
  return rest as InsightSpec;
};

/**
 * A spec with its conditions in one order and nothing empty left on it.
 *
 * Absent rather than empty, so a card with no conditions hashes exactly as it
 * did before conditions existed. Every layout already on a device stays the
 * same card.
 *
 * Exported because the business layout hosts a record spec now, and a hosted
 * card's id has to be the hash of the same normalisation — otherwise the same
 * question placed twice would be two cards.
 */
export const normalizedSpec = (spec: InsightSpec): InsightSpec => {
  const filters = sortFilters(spec.filters);
  return (filters === undefined ? omitFilters(spec) : { ...spec, filters }) as InsightSpec;
};

/**
 * One entry, with its id derived rather than passed.
 *
 * The conditions are put in one order first, so the same two conditions asked
 * in either order are the same card. That normalisation happens here, at the
 * one place an entry is built, rather than at every call site that could
 * assemble a spec.
 */
export const layoutEntry = (
  card: InsightCardId,
  spec: InsightSpec,
  span: InsightSpan = defaultSpanFor(card),
  size: InsightSize = defaultSizeFor(card),
  labelKey: string | null = null,
  title: string | null = null,
): InsightLayoutEntry => {
  const normalized = normalizedSpec(spec);
  return {
    id: specHash(card, normalized),
    card,
    spec: normalized,
    span: allowsSpan(card, span) ? span : defaultSpanFor(card),
    size: allowsSize(card, size) ? size : defaultSizeFor(card),
    labelKey,
    title: title === null || title.trim() === '' ? null : title.trim(),
  };
};


/* ------------------------------------------------- the template's summaries */

/** The scopes a stored summary tile may name. Anything else drops the tile. */
const SCOPES: readonly string[] = ['day', 'week', 'month', 'year', 'all'];

const asScope = (value: unknown): InsightScope | null =>
  typeof value === 'string' && SCOPES.includes(value) ? (value as InsightScope) : null;

/** `sum`, `avg`, `count`, `min`, `max`. `argmax` is handled on its own. */
const AGGS: readonly string[] = ['sum', 'avg', 'count', 'min', 'max'];

/**
 * A field id in a stored expr. The template's own key was `[a-z][a-z0-9_]*`
 * and `spec-apply.ts` rewrote it into a uuid, so dashes must be accepted
 * here even though they never appear in a template file.
 */
const EXPR = /^([a-z]+):([A-Za-z0-9_-]+)$/;
const ARGMAX = /^argmax:([A-Za-z0-9_-]+) by ([a-z]+):([A-Za-z0-9_-]+)$/;

/**
 * Read one summary tile's `expr` into a spec.
 *
 * `measureFieldId` is the record's measure binding: an expr over that field is
 * the net signed number the mirror already materialized, so it compiles to
 * `signed_value` rather than to the field's own column. Anything unparseable
 * answers null and the caller drops the tile — `views[].spec` is typed
 * `object`, and the closed vocabulary is only enforced server-side at
 * spec-apply time.
 */
export const parseSummaryExpr = (
  expr: unknown,
  scope: unknown,
  measureFieldId: string | null,
): ScalarSpec | null => {
  if (typeof expr !== 'string') return null;
  const window = asScope(scope);
  if (window === null) return null;

  const measureOf = (agg: string, fieldId: string): InsightMeasure | null => {
    if (!AGGS.includes(agg)) return null;
    const of = fieldId === measureFieldId ? { kind: 'measure' as const } : { kind: 'field' as const, fieldId };
    return { agg: agg as 'sum' | 'avg' | 'count' | 'min' | 'max', of };
  };

  const leader = ARGMAX.exec(expr);
  if (leader !== null) {
    const [, groupFieldId, agg, fieldId] = leader;
    const measure = measureOf(agg!, fieldId!);
    if (measure === null) return null;
    return { shape: 'scalar', measure, scope: window, leaderOf: groupFieldId! };
  }

  const plain = EXPR.exec(expr);
  if (plain === null) return null;
  const [, agg, fieldId] = plain;
  const measure = measureOf(agg!, fieldId!);
  if (measure === null) return null;
  return { shape: 'scalar', measure, scope: window };
};

/** The field id the measure binding names, or null when the record has none. */
export const measureFieldOf = (schema: RecordSchemaResponse): string | null => {
  const binding = schema.bindings.measure;
  return binding !== undefined && 'fieldId' in binding ? binding.fieldId : null;
};

/**
 * One tile of a template's summary view.
 *
 * `label_key`, `expr`, `scope` and `style` are the original four. The rest were
 * added so a template can pin a chart and not only a number, and every one of
 * them is optional — a tile written before they existed still parses exactly
 * as it did.
 *
 *   card      which card draws it. Absent means `insight.stat`.
 *   group_by  the field to break down by, or to rank. A field id: the template
 *             writes a field key and `spec-apply.ts` rewrites it.
 *   days      how many days a series covers.
 *   limit     how many rows a breakdown draws before the tail is netted.
 *   where     the card's conditions, already in the shape `insight-query.ts`
 *             compiles. The template writes field keys and option keys; apply
 *             rewrites both to ids, so there is one grammar here and not two.
 *   label     the tile's name as a literal word, beside `label_key`. Written
 *             by a setup composed from a person's own words, whose keys this
 *             device does not hold. When present it is the name, and the key
 *             beside it is never looked up.
 *
 * The last three were added when a formula stopped being one column.
 *
 *   measure    what to add up, as an object rather than as the `agg:field`
 *              string. `{agg, of}` where `of` is a row source, `{agg:'rows'}`,
 *              or `{agg:'formula', expr}` for totals combined with arithmetic.
 *              When it is there it WINS over `expr`, which stays valid and is
 *              read whenever `measure` is absent — every tile written before
 *              this key still parses exactly as it did.
 *   group_via  a reference on this record, so the keys are a field on the row
 *              it points at: by the customer's area, not by the customer.
 *   leader_via the same, for the field a scalar ranks by.
 *   date_field a date field of this record whose day the period is read on,
 *              in place of the entry's day (F20 of docs/plans/field-relations.md).
 */
export type SummaryTile = {
  label_key?: unknown;
  label?: unknown;
  expr?: unknown;
  scope?: unknown;
  style?: unknown;
  card?: unknown;
  group_by?: unknown;
  days?: unknown;
  limit?: unknown;
  where?: unknown;
  measure?: unknown;
  group_via?: unknown;
  leader_via?: unknown;
  date_field?: unknown;
};

/** The tiles of the record's summary view, in the order the template wrote them. */
const summaryTiles = (schema: RecordSchemaResponse): readonly SummaryTile[] => {
  const view = schema.views.find((candidate) => candidate.type === 'summary');
  if (view === undefined) return [];
  const summaries = (view.spec as { summaries?: unknown }).summaries;
  return Array.isArray(summaries) ? (summaries as SummaryTile[]) : [];
};

const DEFAULT_SERIES_DAYS = 14;
const DEFAULT_BREAKDOWN_LIMIT = 4;

const positiveInt = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 1 ? Math.trunc(value) : fallback;

/**
 * One tile, as the card it names and the spec that card reads.
 *
 * The card decides the shape, and the shape decides which of the tile's keys
 * matter — that is the same rule the editor's card picker follows, so a
 * template and a person assembling a card by hand cannot produce two different
 * meanings from the same words.
 *
 * Null when the tile cannot be drawn: an unknown card, an expr that will not
 * parse, a breakdown with nothing to group by. A tile is dropped, never
 * guessed at, because a guessed card shows a number under a name that promised
 * something else.
 */
export const parseSummaryTile = (
  tile: SummaryTile,
  measureFieldId: string | null,
): { readonly card: InsightCardId; readonly spec: InsightSpec } | null => {
  const card = tile.card === undefined ? 'insight.stat' : tile.card;
  if (!isInsightCardId(card)) return null;
  const shape = INSIGHT_CARD_SHAPE[card];

  // A series counts days and has no window to name, so a trend tile may leave
  // the scope out. Every other shape must still say one: a missing window is a
  // template bug, and guessing at it would put a number under a name that
  // promised a different period.
  const scope = shape === 'series' && tile.scope === undefined ? 'month' : tile.scope;

  // `measure` wins when it is there, and a `measure` this build cannot read
  // drops the tile rather than falling back to `expr` — a tile that says two
  // things and is drawn from the older one is a number under the wrong name.
  const stated = tile.measure === undefined ? undefined : readTileMeasure(tile.measure);
  if (stated === null) return null;

  const read = ((): { measure: InsightMeasure; scope: InsightScope; leaderOf?: string } | null => {
    if (stated === undefined) {
      const scalar = parseSummaryExpr(tile.expr, scope, measureFieldId);
      return scalar === null
        ? null
        : { measure: scalar.measure, scope: scalar.scope, leaderOf: scalar.leaderOf };
    }
    const window = asScope(scope);
    if (window === null) return null;
    // With a stated measure there is no `argmax:` to read a ranking off, so a
    // scalar takes the group the tile names for it.
    const leaderOf = typeof tile.group_by === 'string' && tile.group_by !== '' ? tile.group_by : undefined;
    return { measure: stated, scope: window, leaderOf };
  })();
  if (read === null) return null;
  const { measure, leaderOf } = read;

  /**
   * The tile's conditions, or null when it carries something unreadable.
   *
   * Null drops the whole tile, as one bad condition drops a whole stored card:
   * a tile called "Open dues" that quietly loses its condition would count the
   * settled ones too, under a name that promised otherwise.
   */
  const conditions = ((): readonly InsightFilter[] | null | undefined => {
    if (tile.where === undefined) return undefined;
    if (!Array.isArray(tile.where)) return null;
    if (!tile.where.every(isFilter)) return null;
    return tile.where as readonly InsightFilter[];
  })();
  if (conditions === null) return null;
  // The date the period is read on rides with the conditions, absent unless the tile said one (F20).
  const dated = typeof tile.date_field === 'string' && tile.date_field !== '' ? { dateField: tile.date_field } : {};
  const filtered = { ...(conditions === undefined ? {} : { filters: conditions }), ...dated };

  // Absent unless the tile said so, so every tile written before these keys
  // hashes to the byte it hashed to before.
  const hop = (value: unknown, key: 'groupVia' | 'leaderVia'): Record<string, string> =>
    typeof value === 'string' && value !== '' ? { [key]: value } : {};

  switch (shape) {
    case 'scalar':
      return {
        card,
        spec: {
          shape: 'scalar',
          measure,
          scope: read.scope,
          ...(leaderOf === undefined ? {} : { leaderOf }),
          ...(leaderOf === undefined ? {} : hop(tile.leader_via, 'leaderVia')),
          ...filtered,
        },
      };
    case 'scalar_pair':
      // There is no period before all of time, so a pair over 'all' is not a
      // question. The template said something impossible; say nothing.
      if (read.scope === 'all') return null;
      return { card, spec: { shape: 'scalar_pair', measure, scope: read.scope, ...filtered } };
    case 'series':
      return {
        card,
        spec: { shape: 'series', measure, days: positiveInt(tile.days, DEFAULT_SERIES_DAYS), ...filtered },
      };
    case 'breakdown': {
      // `argmax:g by ...` already named a group, so a breakdown may reuse it
      // and a template does not have to say the same field twice.
      const groupFieldId =
        typeof tile.group_by === 'string' && tile.group_by !== ''
          ? tile.group_by
          : (leaderOf ?? null);
      if (groupFieldId === null) return null;
      return {
        card,
        spec: {
          shape: 'breakdown',
          measure,
          groupFieldId,
          ...hop(tile.group_via, 'groupVia'),
          scope: read.scope,
          limit: positiveInt(tile.limit, DEFAULT_BREAKDOWN_LIMIT),
          ...filtered,
        },
      };
    }
  }
};

/**
 * A tile's `measure` object, read back without trusting it.
 *
 * Null means this build cannot draw what it asks for, and the caller drops the
 * tile. The row source and the aggregate tree are read by the SHARED readers,
 * so a card the assistant proposed, a card a template pinned and a card a
 * person built are all read by one function.
 */
const readTileMeasure = (raw: unknown): InsightMeasure | null => {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const held = raw as Record<string, unknown>;
  if (held.agg === 'rows') return { agg: 'rows' };
  if (held.agg === 'formula') {
    const expr = readInsightAgg(held.expr);
    return expr === null ? null : { agg: 'formula', expr };
  }
  if (typeof held.agg !== 'string' || !AGGS.includes(held.agg)) return null;
  const of = readRowSource(held.of);
  return of === null ? null : { agg: held.agg as 'sum' | 'avg' | 'count' | 'min' | 'max', of };
};

/**
 * What a record shows before anybody customizes it: the cards its own template
 * asked for.
 *
 * A `hero` tile takes the whole row, which is what `style` already meant, and a
 * card that plots takes the whole row whatever the tile said — `layoutEntry`
 * enforces that, so this only has to pass on the preference.
 */
export const defaultLayoutFor = (schema: RecordSchemaResponse): InsightLayoutEntry[] => {
  const measureFieldId = measureFieldOf(schema);
  const entries: InsightLayoutEntry[] = [];
  const seen = new Set<string>();
  for (const tile of summaryTiles(schema)) {
    const parsed = parseSummaryTile(tile, measureFieldId);
    if (parsed === null) continue;
    // A literal word is the person's own, so it rides as `title`, which wins
    // over a key and sits outside the hash exactly as a typed name does. The
    // key beside it is dropped rather than kept: nothing here could resolve
    // a composed key, and a stored key nothing resolves is a crash waiting.
    const literal = typeof tile.label === 'string' && tile.label.trim() !== '' ? tile.label : null;
    const entry = layoutEntry(
      parsed.card,
      parsed.spec,
      tile.style === 'hero' ? 'full' : 'half',
      // A hero is the page's headline, so it takes the whole row either way.
      // Everything else takes its card's default — except a leader, which
      // answers a NAME and cannot live in one cell.
      tile.style === 'hero' || isLeaderSpec(parsed.spec) ? 'wide' : defaultSizeFor(parsed.card),
      literal === null && typeof tile.label_key === 'string' ? tile.label_key : null,
      literal,
    );
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    entries.push(entry);
  }
  return entries;
};

/* ------------------------------------------------------------ reading back */

const SPANS: readonly string[] = ['half', 'full'];
const SIZES: readonly string[] = ['small', 'wide', 'large'];

/**
 * Whether this spec answers a NAME rather than a figure — "Top item", and the
 * only reason a stat is ever placed Wide by default.
 *
 * It is the same test `insight-present.ts` makes twice (:482 and :567) to draw
 * a leader's word instead of its number, written here rather than invented:
 * a scalar that names what it is the leader OF. It is not exported from there,
 * and this step does not rewrite that file to export it.
 */
export const isLeaderSpec = (spec: InsightSpec): boolean =>
  spec.shape === 'scalar' && spec.leaderOf !== undefined;

/**
 * One stored condition, checked field by field.
 *
 * A presence op carries no value; everything else must carry a string or a
 * finite number; a `via` names the reference the condition reads through.
 * Anything else is not a condition this build can compile.
 *
 * The shared reader, not a second one. A condition rides in a stored layout, in
 * a template's tile and in an assistant's proposal, and two readers of one
 * shape would drift into two products.
 */
const isFilter = (value: unknown): value is InsightFilter => readInsightWhere(value) !== null;

/**
 * Whether every condition on a stored spec is one this build can compile.
 *
 * One bad condition drops the WHOLE card, not just that condition. Dropping
 * only the condition would leave a card called "Credit sales" showing every
 * sale — a bigger number under a name that promised a smaller one, which is
 * the one failure worse than a missing card.
 */
const filtersAreSound = (value: unknown): boolean => {
  if (value === undefined) return true;
  if (!Array.isArray(value)) return false;
  return value.every(isFilter);
};

/** A hop on a stored spec: absent, or a field id. Never anything else. */
const hopIsSound = (value: unknown): boolean =>
  value === undefined || (typeof value === 'string' && value !== '');

/**
 * A spec is data on disk that an older or newer build may have written.
 *
 * Exported because the Dashboard hosts a record spec now, and the business
 * layout has to read that half back with the same reader — one grammar, read
 * once, or a card would be accepted on one screen and dropped on the other.
 */
export const isRecordSpec = (value: unknown): value is InsightSpec => {
  if (value === null || typeof value !== 'object') return false;
  const spec = value as Record<string, unknown>;
  if (readTileMeasure(spec.measure) === null) return false;
  if (!filtersAreSound(spec.filters)) return false;
  if (!hopIsSound(spec.dateField)) return false;
  switch (spec.shape) {
    case 'scalar':
      return asScope(spec.scope) !== null && hopIsSound(spec.leaderVia);
    case 'scalar_pair':
      return asScope(spec.scope) !== null && spec.scope !== 'all';
    case 'series':
      return typeof spec.days === 'number' && Number.isFinite(spec.days);
    case 'breakdown':
      return (
        asScope(spec.scope) !== null &&
        typeof spec.groupFieldId === 'string' &&
        typeof spec.limit === 'number' &&
        hopIsSound(spec.groupVia)
      );
    default:
      return false;
  }
};

/**
 * Read a stored layout, dropping anything this build cannot draw.
 *
 * Unparseable JSON answers null, which the caller treats as "no layout" and
 * falls back to the template's default — the safe direction. A single bad
 * entry inside good JSON is dropped on its own, so one unknown card from a
 * newer build does not blank the strip.
 */
export const parseLayout = (json: string): InsightLayoutEntry[] | null => {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!Array.isArray(raw)) return null;

  const entries: InsightLayoutEntry[] = [];
  const seen = new Set<string>();
  for (const held of raw) {
    if (held === null || typeof held !== 'object') continue;
    const row = held as Record<string, unknown>;
    if (!isInsightCardId(row.card) || !isRecordSpec(row.spec)) continue;
    const span = typeof row.span === 'string' && SPANS.includes(row.span) ? (row.span as InsightSpan) : undefined;
    /*
     * The size, in three falls. A readable `size` this card allows wins. With
     * none — every layout written before this key existed — the SPAN that was
     * just read is migrated: half was one cell, full was a whole row, and a
     * breakdown at full row was always the tall one. With neither, the card's
     * own default.
     *
     * An unreadable size drops the SIZE, never the insight: the rule the span
     * above already follows.
     */
    const stored = typeof row.size === 'string' && SIZES.includes(row.size) ? (row.size as InsightSize) : undefined;
    const migrated: InsightSize | undefined =
      span === undefined ? undefined : span === 'half' ? 'small' : row.card === 'insight.breakdown' ? 'large' : 'wide';
    const size = stored !== undefined && allowsSize(row.card, stored) ? stored : migrated;
    const entry = layoutEntry(
      row.card,
      row.spec,
      span,
      size,
      typeof row.labelKey === 'string' ? row.labelKey : null,
      typeof row.title === 'string' ? row.title : null,
    );
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    entries.push(entry);
  }
  return entries;
};

/* --------------------------------------------------------------- mutations */

/**
 * The six edits, over any placed card.
 *
 * Generic in the entry, because a business layout is the same list of the same
 * shape over a different spec grammar, and two copies of "move one card" is two
 * places for the index arithmetic to be wrong. The record layout's own tests
 * pass unchanged, which is what proves the widening changed no behaviour.
 */
type Placed = { readonly id: string };

/**
 * How many insights one RECORD may hold (`REDESIGN.md` §5 and §7).
 *
 * It is passed to `withCard` rather than baked into it, because the same
 * mutation serves the Dashboard's layout, which §5 leaves exactly as it ships.
 */
export const INSIGHT_CAP = 12;

/**
 * Put a card in, unless the same card over the same insight is already there.
 *
 * `at` inserts rather than appends — the grid drops an insight where the
 * finger let go, and the gallery adds after the card being looked at.
 *
 * `cap` refuses a NEW card once the layout is that long. It refuses rather
 * than trimming: the layout is the person's, and dropping one of theirs to
 * make room for another would be a silent edit. The dedupe above still wins,
 * so a card already placed is a no-op at the cap as it is below it — one cap,
 * in one mutation, so every path in obeys it.
 */
export const withCard = <E extends Placed>(
  layout: readonly E[],
  entry: E,
  { at, cap }: { readonly at?: number; readonly cap?: number } = {},
): E[] => {
  if (layout.some((held) => held.id === entry.id)) return [...layout];
  if (cap !== undefined && layout.length >= cap) return [...layout];
  if (at === undefined || at < 0 || at > layout.length) return [...layout, entry];
  const next = [...layout];
  next.splice(at, 0, entry);
  return next;
};

export const withoutCard = <E extends Placed>(layout: readonly E[], id: string): E[] =>
  layout.filter((held) => held.id !== id);

/** Move one card to another position. An index outside the list is a no-op. */
export const withMoved = <E extends Placed>(layout: readonly E[], from: number, to: number): E[] => {
  if (from === to) return [...layout];
  if (from < 0 || from >= layout.length || to < 0 || to >= layout.length) return [...layout];
  const next = [...layout];
  const [lifted] = next.splice(from, 1);
  if (lifted === undefined) return [...layout];
  next.splice(to, 0, lifted);
  return next;
};

/**
 * Rename one card, or take its name away.
 *
 * The id does not move, because the name is not part of what the card draws.
 * An empty name is not a name: it clears back to the template's word, or to
 * the derived sentence.
 */
/**
 * Resize one card, if its kind allows that size. An illegal size is left
 * alone rather than corrected, the way `withSpan` leaves an illegal span:
 * nothing in the grid can ask for one, so a request for one is a defect
 * upstream and silently rewriting it would hide it.
 */
export const withSize = <
  E extends Placed & { readonly card: InsightCardId; readonly size: InsightSize },
>(
  layout: readonly E[],
  id: string,
  size: InsightSize,
): E[] =>
  layout.map((held) => (held.id === id && allowsSize(held.card, size) ? { ...held, size } : held));

export const withTitle = <E extends Placed & { readonly title: string | null }>(
  layout: readonly E[],
  id: string,
  title: string | null,
): E[] =>
  layout.map((held) =>
    held.id === id
      ? { ...held, title: title === null || title.trim() === '' ? null : title.trim() }
      : held,
  );

/**
 * Put a changed card where the old one stood.
 *
 * Editing a placed card's period changes what it draws, so it changes its id.
 * Removing and appending would send it to the bottom of the strip, which is
 * not what changing a period asked for — so it is replaced in place.
 *
 * If the new card is already somewhere else on the list, that copy is dropped:
 * the same question twice is still one card, and the position being edited is
 * the one the person is looking at.
 */
export const withReplaced = <E extends Placed>(
  layout: readonly E[],
  id: string,
  entry: E,
): E[] => {
  if (!layout.some((held) => held.id === id)) return [...layout, entry];
  const next: E[] = [];
  for (const held of layout) {
    if (held.id === id) {
      next.push(entry);
      continue;
    }
    if (held.id === entry.id) continue;
    next.push(held);
  }
  return next;
};

/** Set one card's width, ignoring a width that card does not allow. */
export const withSpan = <
  E extends Placed & { readonly card: InsightCardId; readonly span: InsightSpan },
>(
  layout: readonly E[],
  id: string,
  span: InsightSpan,
): E[] =>
  layout.map((held) =>
    held.id === id && allowsSpan(held.card, span) ? { ...held, span } : held,
  );
